import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import type {useI18n} from '@/i18n'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

type I18n = ReturnType<typeof useI18n>

export const OutboundConnectorType = {
    SCIM_2_0: 'SCIM_2_0',
    LDAP: 'LDAP',
    WEBHOOK: 'WEBHOOK',
    EMAIL: 'EMAIL',
    MANUAL: 'MANUAL',
    REST: 'REST',
    DATABASE: 'DATABASE',
    KUBERNETES: 'KUBERNETES',
    POLLING: 'POLLING',
    SCRIPT: 'SCRIPT',
    SAP_ABAP: 'SAP_ABAP',
} as const;

export const AuthType = {
    BEARER_TOKEN: 'BEARER_TOKEN',
    BASIC_AUTH: 'BASIC_AUTH',
    OAUTH2_CLIENT: 'OAUTH2_CLIENT',
    TOKEN: 'TOKEN',
    CERTIFICATE: 'CERTIFICATE',
    KUBECONFIG: 'KUBECONFIG',
    NONE: 'NONE',
} as const;
export type AuthType = typeof AuthType[keyof typeof AuthType];

/**
 * Rich enum carrying UI metadata (badge variant, i18n label) for
 * {@link AuthType} wire values. Mirrors the `DelegationStatus` /
 * `DelegationStatusBadge` pairing from issue 214.
 */
export class AuthTypeBadge extends EnumValue {
    static readonly BEARER_TOKEN = new AuthTypeBadge(
        AuthType.BEARER_TOKEN,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.authTypeLabels.bearerToken as string,
    )
    static readonly BASIC_AUTH = new AuthTypeBadge(
        AuthType.BASIC_AUTH,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.authTypeLabels.basicAuth as string,
    )
    static readonly OAUTH2_CLIENT = new AuthTypeBadge(
        AuthType.OAUTH2_CLIENT,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.authTypeLabels.oauth2Client as string,
    )
    static readonly TOKEN = new AuthTypeBadge(
        AuthType.TOKEN,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.authTypeLabels.token as string,
    )
    static readonly CERTIFICATE = new AuthTypeBadge(
        AuthType.CERTIFICATE,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.authTypeLabels.certificate as string,
    )
    static readonly KUBECONFIG = new AuthTypeBadge(
        AuthType.KUBECONFIG,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.authTypeLabels.kubeconfig as string,
    )
    static readonly NONE = new AuthTypeBadge(
        AuthType.NONE,
        BadgeVariant.OUTLINE,
        (i18n: I18n) => i18n.connectors.authTypeLabels.none as string,
    )

    static readonly values: ReadonlyArray<AuthTypeBadge> = [
        AuthTypeBadge.BEARER_TOKEN,
        AuthTypeBadge.BASIC_AUTH,
        AuthTypeBadge.OAUTH2_CLIENT,
        AuthTypeBadge.TOKEN,
        AuthTypeBadge.CERTIFICATE,
        AuthTypeBadge.KUBECONFIG,
        AuthTypeBadge.NONE,
    ]

    static of(code: AuthType | null | undefined): AuthTypeBadge | undefined {
        return AuthTypeBadge.values.find((v) => v.code === code)
    }
}

export const MappingType = {
    DIRECT: 'DIRECT',
    COMPUTED: 'COMPUTED',
    STATIC: 'STATIC',
} as const;
export type MappingType = typeof MappingType[keyof typeof MappingType];

/**
 * Rich enum carrying UI metadata (badge variant, i18n label) for
 * {@link MappingType} wire values. Mirrors the {@link AuthTypeBadge}
 * pattern from issue 385.
 */
export class MappingTypeBadge extends EnumValue {
    static readonly DIRECT = new MappingTypeBadge(
        MappingType.DIRECT,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.attributeMappings.typeDirect as string,
    )
    static readonly COMPUTED = new MappingTypeBadge(
        MappingType.COMPUTED,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.attributeMappings.typeComputed as string,
    )
    static readonly STATIC = new MappingTypeBadge(
        MappingType.STATIC,
        BadgeVariant.DEFAULT,
        (i18n: I18n) => i18n.connectors.attributeMappings.typeStatic as string,
    )

    static readonly values: ReadonlyArray<MappingTypeBadge> = [
        MappingTypeBadge.DIRECT,
        MappingTypeBadge.COMPUTED,
        MappingTypeBadge.STATIC,
    ]

    static of(code: MappingType | null | undefined): MappingTypeBadge | undefined {
        return MappingTypeBadge.values.find((v) => v.code === code)
    }
}

/**
 * Wrapper for the GET/PUT /attribute-mappings endpoint.
 * Matches Java OutboundConnectorAttributeMappingListResponse.
 */
export interface OutboundConnectorAttributeMappingList {
  attributeMappings: OutboundConnectorAttributeMapping[] | null;
}

export type OutboundConnectorAttributeMappingResponse = OutboundConnectorAttributeMappingList;

/**
 * Request wrapper for PUT /attribute-mappings endpoint.
 * Matches Java OutboundConnectorAttributeMappingRequest. Issue 571.07 expanded
 * the entry shape to include targetEntityType / identifierType / transform —
 * the same fields the inbound side already carries.
 */
export interface OutboundConnectorAttributeMappingRequest {
  attributeMappings: OutboundOutboundConnectorAttributeMappingRequestEntry[] | null;
}

export interface OutboundOutboundConnectorAttributeMappingRequestEntry {
  source: string | null;
  target: string | null;
  targetEntityType: string | null;
  identifierType: string | null;
  transform: string | null;
  type: string | null;
  required: boolean;
  transformArgs: Record<string, unknown> | null;
}

/**
 * Attribute mapping config for a connector (shared with inbound connectors).
 * Issue 571.07: rows now flow through the outbound_attribute_mappings child
 * table, so the wire shape carries the full {@link InboundAttributeMapping} fields.
 */
export interface OutboundConnectorAttributeMapping {
  source: string;
  target: string;
  targetEntityType: string | null;
  identifierType: string | null;
  transform: string | null;
  type: MappingType | null;
  required: boolean;
  transformArgs: Record<string, unknown> | null;
}

/**
 * Type-level default attribute mapping seeded into a new connector at create
 * time. Rows live in {@code outbound_connector_type_default_mappings}; the
 * mock store reads them and copies the matching rows into a new connector's
 * {@link OutboundConnectorAttributeMapping} list on POST.
 */
export interface OutboundConnectorTypeDefaultMapping {
  outboundConnectorType: string;
  sequence: number;
  source: string;
  target: string;
  targetEntityType: string;
  mappingType: MappingType | null;
  required: boolean;
}

/**
 * OU routing rule for LDAP provisioning. Issue 571.08 — wire-shape uses
 * the `targetOu` field name (mirroring the backend's
 * {@code OuRoutingRuleResponse}). Persisted as one row of
 * {@code ldap_ou_routing_rules}.
 */
export interface OuRoutingRule {
  attribute: string | null;
  operator: string | null;
  value: string | null;
  targetOu: string | null;
}

/**
 * Allowed operator values for {@link OuRoutingRule}. Mirrors the backend
 * {@code LdapOuRoutingOperator} enum.
 */
export const LdapOuRoutingOperator = {
    EQUALS: 'EQUALS',
    NOT_EQUALS: 'NOT_EQUALS',
    CONTAINS: 'CONTAINS',
    STARTS_WITH: 'STARTS_WITH',
    IN: 'IN',
} as const;
export type LdapOuRoutingOperator = typeof LdapOuRoutingOperator[keyof typeof LdapOuRoutingOperator];

/**
 * Connector response from the API (list view).
 */
export interface OutboundConnectorList {
  id: UUID;
  targetSystem: EntityLink;
  connectorType: string;
  enabled: boolean;
  attributeMappings: OutboundConnectorAttributeMapping[] | null;
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * SCIM connector configuration response from the API.
 */
export interface ScimOutboundLog {
  id: UUID;
  connectorId: UUID;
  provisioningTaskId: UUID | null;
  operation: string;
  resourceType: string;
  resourceId: string | null;
  requestUrl: string;
  responseStatus: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: Instant;
}

export interface ScimOutboundConfig {
  id: UUID;
  connectorId: UUID;
  // Endpoint
  baseUrl: string;
  userEndpoint: string;
  groupEndpoint: string;
  userIdAttribute: string;
  groupIdAttribute: string;
  // Authentication
  authType: AuthType;
  tokenUrl: string | null;
  clientId: string | null;
  username: string | null;
  scope: string | null;
  // HTTP Configuration
  connectTimeoutMs: number;
  readTimeoutMs: number;
  maxRetries: number;
  retryBackoffMs: number;
  // Features
  supportsBulk: boolean;
  supportsEtag: boolean;
  patchStyle: string;
  // Metadata
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * LDAP connector configuration response from the API. Issue 571.08 dropped the
 * legacy {@code ldapProvisioningConfig} JSONB nested object and lifted its three
 * fields into the flat {@code dnTemplate}, {@code defaultOu}, {@code objectClasses}
 * (child rows), and {@code ouRoutingRules} (child rows) shape.
 */
export interface LdapOutboundConfig {
  id: UUID;
  host: string;
  port: number;
  useSsl: boolean;
  useStarttls: boolean;
  bindDn: string;
  baseDn: string;
  userSearchBase: string | null;
  userSearchFilter: string | null;
  groupSearchBase: string | null;
  groupMemberAttribute: string;
  userDnAttribute: string;
  connectTimeoutMs: number;
  responseTimeoutMs: number;
  poolSize: number;
  dnTemplate: string | null;
  defaultOu: string | null;
  directoryDialect: DirectoryDialect;
  objectClasses: string[];
  ouRoutingRules: OuRoutingRule[];
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * Directory-server flavour (issue 755). Drives DISABLE_ACCOUNT / ENABLE_ACCOUNT
 * wire shape: ACTIVE_DIRECTORY emits userAccountControl=514/512, OPEN_LDAP emits
 * nsAccountLock=TRUE (or removed). Default for new connectors is ACTIVE_DIRECTORY
 * to preserve the pre-755 wire contract.
 */
export const DirectoryDialect = {
    ACTIVE_DIRECTORY: 'ACTIVE_DIRECTORY',
    OPEN_LDAP: 'OPEN_LDAP',
} as const;
export type DirectoryDialect = typeof DirectoryDialect[keyof typeof DirectoryDialect];

/**
 * Header name/value pair for a REST outbound operation (issue 571.09).
 */
export interface RestHeaderEntry {
  name: string;
  value: string;
}

/**
 * REST connector configuration response from the API.
 */
export interface RestOutboundConfig {
  id: UUID;
  // CPD-OFF — response and update request types have overlapping fields by design
  baseUrl: string;
  authType: AuthType;
  authSecretRef: string | null;
  tokenUrl: string | null;
  clientId: string | null;
  clientSecretRef: string | null;
  scope: string | null;
  grantMethod: string;
  grantUrlTemplate: string;
  grantBodyTemplate: string | null;
  grantHeaders: RestHeaderEntry[];
  revokeMethod: string;
  revokeUrlTemplate: string;
  revokeBodyTemplate: string | null;
  revokeHeaders: RestHeaderEntry[];
  successStatusCodes: string | null;
  contentType: string | null;
  connectTimeoutMs: number;
  readTimeoutMs: number;
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

export const DatabaseType = {
    POSTGRESQL: 'POSTGRESQL',
    MYSQL: 'MYSQL',
    ORACLE: 'ORACLE',
    SQLSERVER: 'SQLSERVER',
    OTHER: 'OTHER',
} as const;
export type DatabaseType = typeof DatabaseType[keyof typeof DatabaseType];

/**
 * Database connector configuration response from the API.
 */
export interface DatabaseOutboundConfig {
  id: UUID;
  databaseType: DatabaseType;
  jdbcUrl: string;
  driverClass: string;
  username: string;
  passwordRef: string;
  grantSqlTemplate: string;
  revokeSqlTemplate: string;
  testSql: string | null;
  connectTimeoutMs: number;
  queryTimeoutMs: number;
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * Kubernetes connector configuration response from the API.
 */
export interface K8sOutboundConfig {
  id: UUID;
  apiServerUrl: string;
  authType: AuthType;
  tokenRef: string | null;
  caCertRef: string | null;
  defaultNamespace: string | null;
  skipTlsVerify: boolean;
  connectTimeoutMs: number;
  requestTimeoutMs: number;
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * Email connector configuration response from the API.
 */
export interface EmailOutboundConfig {
  id: UUID;
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string | null;
  smtpPasswordRef: string | null;
  useTls: boolean;
  fromAddress: string;
  toAddresses: string;
  ccAddresses: string | null;
  grantSubjectTemplate: string;
  grantBodyTemplate: string;
  revokeSubjectTemplate: string;
  revokeBodyTemplate: string;
  bodyContentType: string;
  connectTimeoutMs: number;
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * Connector detail response from the API (includes protocol-specific config).
 */
export interface OutboundConnector extends OutboundConnectorList {
  scimConfig: ScimOutboundConfig | null;
  ldapConfig: LdapOutboundConfig | null;
  restConfig: RestOutboundConfig | null;
  databaseConfig: DatabaseOutboundConfig | null;
  k8sConfig: K8sOutboundConfig | null;
  emailConfig: EmailOutboundConfig | null;
  scriptConfig: ScriptOutboundConfig | null;
}

/**
 * Script connector configuration response.
 * Matches Java ScriptOutboundConfigResponse.
 */
export interface ScriptOutboundConfig {
    id: UUID;
    outboundConnectorId: UUID;
    baseUrl: string | null;
    authType: AuthType;
    tokenUrl: string | null;
    clientId: string | null;
    scope: string | null;
    username: string | null;
    apiKeyHeader: string | null;
    script: string;
    // 571.06: 4 boolean gates replace the legacy enabledOperations Map.
    createEnabled: boolean;
    updateEnabled: boolean;
    deleteEnabled: boolean;
    queryEnabled: boolean;
    connectTimeoutMs: number;
    readTimeoutMs: number;
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

/**
 * Request to create a script connector configuration.
 * Matches Java ScriptOutboundConfigCreateRequest.
 */
export interface ScriptOutboundConfigCreateRequest {
    baseUrl: string | null;
    authType: AuthType;
    authSecretRef: string | null;
    tokenUrl: string | null;
    clientId: string | null;
    clientSecretRef: string | null;
    scope: string | null;
    username: string | null;
    passwordSecretRef: string | null;
    apiKeyHeader: string | null;
    apiKeySecretRef: string | null;
    script: string | null;
    createEnabled: boolean | null;
    updateEnabled: boolean | null;
    deleteEnabled: boolean | null;
    queryEnabled: boolean | null;
    connectTimeoutMs: number | null;
    readTimeoutMs: number | null;
}

/**
 * Request to update a script connector configuration.
 * Matches Java ScriptOutboundConfigUpdateRequest.
 */
export interface ScriptOutboundConfigUpdateRequest {
    version: number;
    baseUrl: string | null;
    authType: AuthType;
    authSecretRef: string | null;
    tokenUrl: string | null;
    clientId: string | null;
    clientSecretRef: string | null;
    scope: string | null;
    username: string | null;
    passwordSecretRef: string | null;
    apiKeyHeader: string | null;
    apiKeySecretRef: string | null;
    script: string | null;
    createEnabled: boolean | null;
    updateEnabled: boolean | null;
    deleteEnabled: boolean | null;
    queryEnabled: boolean | null;
    connectTimeoutMs: number | null;
    readTimeoutMs: number | null;
}

/**
 * Request to validate a Groovy script.
 * Matches Java ScriptValidateRequest (outbound).
 */
export interface ScriptValidateRequest {
    script: string;
}

/**
 * Response from the script validate endpoint.
 * Matches Java ScriptValidationResult.
 */
export interface ScriptValidationError {
    line: number;
    column: number;
    message: string;
}

export interface ScriptValidationResult {
    valid: boolean;
    errors: ScriptValidationError[];
    supportedOperations: string[];
    unsupportedOperations: string[];
    warnings: string[];
}

/**
 * Generic connector log entry response from the API.
 */
export interface OutboundConnectorLog {
  id: UUID;
  connectorId: UUID;
  provisioningTaskId: UUID | null;
  connectorType: string;
  operation: string;
  success: boolean;
  statusCode: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  batchId: UUID | null;
  createdAt: Instant;
}

/**
 * Request to create a new connector.
 */
export interface OutboundConnectorCreateRequest {
  targetSystemId: UUID;
  connectorType: string;
  enabled: boolean;
}

/**
 * Request to update a connector.
 */
export interface OutboundConnectorUpdateRequest {
  enabled: boolean;
  version: number;
}

/**
 * SCIM authentication configuration (nested in create/update requests).
 */
export interface ScimAuthConfig {
  authType: AuthType;
  tokenSecretRef?: string;
  tokenUrl?: string;
  clientId?: string;
  clientSecretRef?: string;
  scope?: string;
  username?: string;
  passwordSecretRef?: string;
}

/**
 * SCIM HTTP configuration (nested in create/update requests).
 */
export interface ScimHttpConfig {
  connectTimeoutMs?: number;
  readTimeoutMs?: number;
  maxRetries?: number;
  retryBackoffMs?: number;
}

/**
 * Request to create SCIM connector configuration.
 */
export interface ScimOutboundConfigCreateRequest {
  baseUrl: string;
  userEndpoint?: string;
  groupEndpoint?: string;
  userIdAttribute?: string;
  groupIdAttribute?: string;
  auth: ScimAuthConfig;
  http?: ScimHttpConfig;
  supportsBulk?: boolean;
  supportsEtag?: boolean;
  patchStyle?: string;
}

/**
 * Request to update SCIM connector configuration.
 */
export interface ScimOutboundConfigUpdateRequest {
  baseUrl: string;
  userEndpoint: string;
  groupEndpoint: string;
  userIdAttribute: string;
  groupIdAttribute: string;
  auth: ScimAuthConfig;
  http: ScimHttpConfig;
  supportsBulk: boolean;
  supportsEtag: boolean;
  patchStyle: string;
  version: number;
}

/**
 * Request to create LDAP connector configuration. Issue 571.08 — flat fields
 * replace the legacy nested {@code ldapProvisioningConfig} object.
 */
export interface LdapOutboundConfigCreateRequest {
  host: string;
  port?: number;
  useSsl?: boolean;
  useStarttls?: boolean;
  bindDn: string;
  bindPasswordRef: string;
  baseDn: string;
  userSearchBase?: string;
  userSearchFilter?: string;
  groupSearchBase?: string;
  groupMemberAttribute?: string;
  userDnAttribute?: string;
  connectTimeoutMs?: number;
  responseTimeoutMs?: number;
  poolSize?: number;
  dnTemplate?: string | null;
  defaultOu?: string | null;
  directoryDialect?: DirectoryDialect | null;
  objectClasses?: string[] | null;
  ouRoutingRules?: OuRoutingRuleRequest[] | null;
}

/**
 * Request to update LDAP connector configuration. Issue 571.08 — flat fields
 * replace the legacy nested {@code ldapProvisioningConfig} object.
 */
export interface LdapOutboundConfigUpdateRequest {
  host: string;
  port: number;
  useSsl: boolean;
  useStarttls: boolean;
  bindDn: string;
  bindPasswordRef: string;
  baseDn: string;
  userSearchBase: string | null;
  userSearchFilter: string | null;
  groupSearchBase: string | null;
  groupMemberAttribute: string;
  userDnAttribute: string;
  connectTimeoutMs: number;
  responseTimeoutMs: number;
  poolSize: number;
  version: number;
  dnTemplate?: string | null;
  defaultOu?: string | null;
  directoryDialect?: DirectoryDialect | null;
  objectClasses?: string[] | null;
  ouRoutingRules?: OuRoutingRuleRequest[] | null;
}

/**
 * Single OU routing rule (request shape). Issue 571.08 — wire field is `targetOu`.
 */
export interface OuRoutingRuleRequest {
  attribute: string | null;
  operator: string | null;
  value: string | null;
  targetOu: string | null;
}

/**
 * Request to create REST connector configuration.
 */
export interface RestOutboundConfigCreateRequest {
  baseUrl: string;
  authType?: AuthType;
  authSecretRef?: string;
  tokenUrl?: string;
  clientId?: string;
  clientSecretRef?: string;
  scope?: string;
  grantMethod?: string;
  grantUrlTemplate: string;
  grantBodyTemplate?: string;
  grantHeaders?: RestHeaderEntry[];
  revokeMethod?: string;
  revokeUrlTemplate: string;
  revokeBodyTemplate?: string;
  revokeHeaders?: RestHeaderEntry[];
  successStatusCodes?: string;
  contentType?: string;
  connectTimeoutMs?: number;
  readTimeoutMs?: number;
}

/**
 * Request to update REST connector configuration.
 */
export interface RestOutboundConfigUpdateRequest {
  baseUrl: string;
  authType: AuthType;
  authSecretRef: string | null;
  tokenUrl: string | null;
  clientId: string | null;
  clientSecretRef: string | null;
  scope: string | null;
  grantMethod: string;
  grantUrlTemplate: string;
  grantBodyTemplate: string | null;
  grantHeaders: RestHeaderEntry[];
  revokeMethod: string;
  revokeUrlTemplate: string;
  revokeBodyTemplate: string | null;
  revokeHeaders: RestHeaderEntry[];
  successStatusCodes: string | null;
  contentType: string | null;
  connectTimeoutMs: number;
  readTimeoutMs: number;
  version: number;
}

/**
 * Request to create database connector configuration.
 */
export interface DatabaseOutboundConfigCreateRequest {
  databaseType: DatabaseType;
  jdbcUrl: string;
  driverClass: string;
  username: string;
  passwordRef: string;
  grantSqlTemplate: string;
  revokeSqlTemplate: string;
  testSql?: string;
  connectTimeoutMs?: number;
  queryTimeoutMs?: number;
}

/**
 * Request to update database connector configuration.
 */
export interface DatabaseOutboundConfigUpdateRequest {
  databaseType: DatabaseType;
  jdbcUrl: string;
  driverClass: string;
  username: string;
  passwordRef: string;
  grantSqlTemplate: string;
  revokeSqlTemplate: string;
  testSql: string | null;
  connectTimeoutMs: number;
  queryTimeoutMs: number;
  version: number;
}

/**
 * Request to create Kubernetes connector configuration.
 */
export interface K8sOutboundConfigCreateRequest {
  apiServerUrl: string;
  authType?: AuthType;
  tokenRef?: string;
  caCertRef?: string;
  defaultNamespace?: string;
  skipTlsVerify?: boolean;
  connectTimeoutMs?: number;
  requestTimeoutMs?: number;
}

/**
 * Request to update Kubernetes connector configuration.
 */
export interface K8sOutboundConfigUpdateRequest {
  apiServerUrl: string;
  authType: AuthType;
  tokenRef: string | null;
  caCertRef: string | null;
  defaultNamespace: string | null;
  skipTlsVerify: boolean;
  connectTimeoutMs: number;
  requestTimeoutMs: number;
  version: number;
}

/**
 * Request to create email connector configuration.
 */
export interface EmailOutboundConfigCreateRequest {
  smtpHost: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPasswordRef?: string;
  useTls?: boolean;
  fromAddress: string;
  toAddresses: string;
  ccAddresses?: string;
  grantSubjectTemplate: string;
  grantBodyTemplate: string;
  revokeSubjectTemplate: string;
  revokeBodyTemplate: string;
  bodyContentType?: string;
  connectTimeoutMs?: number;
}

/**
 * Request to update email connector configuration.
 */
export interface EmailOutboundConfigUpdateRequest {
  smtpHost: string;
  smtpPort: number;
  smtpUsername: string | null;
  smtpPasswordRef: string | null;
  useTls: boolean;
  fromAddress: string;
  toAddresses: string;
  ccAddresses: string | null;
  grantSubjectTemplate: string;
  grantBodyTemplate: string;
  revokeSubjectTemplate: string;
  revokeBodyTemplate: string;
  bodyContentType: string | null;
  connectTimeoutMs: number;
  version: number;
}

/**
 * Paginated response for connectors.
 */
export interface OutboundConnectorPage {
  content: OutboundConnectorList[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * Paginated response for connector logs.
 */
export interface OutboundConnectorLogPage {
  content: OutboundConnectorLog[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * Test connection result.
 */
export interface OutboundConnectorTestResult {
  success: boolean;
  statusCode: number;
  message: string;
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const OutboundConnectorCreateRequestFields = {
    targetSystemId: 'targetSystemId',
    connectorType: 'connectorType',
    enabled: 'enabled',
} as const

export const OutboundConnectorResponseFields = {
    id: 'id',
    targetSystem: 'targetSystem',
    connectorType: 'connectorType',
    enabled: 'enabled',
    attributeMappings: 'attributeMappings',
    scimConfig: 'scimConfig',
    ldapConfig: 'ldapConfig',
    restConfig: 'restConfig',
    databaseConfig: 'databaseConfig',
    k8sConfig: 'k8sConfig',
    emailConfig: 'emailConfig',
    scriptConfig: 'scriptConfig',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const OutboundConnectorLogResponseFields = {
    id: 'id',
    connectorId: 'connectorId',
    provisioningTaskId: 'provisioningTaskId',
    connectorType: 'connectorType',
    operation: 'operation',
    success: 'success',
    statusCode: 'statusCode',
    errorMessage: 'errorMessage',
    durationMs: 'durationMs',
    batchId: 'batchId',
    createdAt: 'createdAt',
} as const

export const OutboundConnectorListResponseFields = {
    id: 'id',
    targetSystem: 'targetSystem',
    connectorType: 'connectorType',
    enabled: 'enabled',
    attributeMappings: 'attributeMappings',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const OutboundConnectorUpdateRequestFields = {
    enabled: 'enabled',
    version: 'version',
} as const

export const DatabaseOutboundConfigCreateRequestFields = {
    databaseType: 'databaseType',
    jdbcUrl: 'jdbcUrl',
    driverClass: 'driverClass',
    username: 'username',
    passwordRef: 'passwordRef',
    grantSqlTemplate: 'grantSqlTemplate',
    revokeSqlTemplate: 'revokeSqlTemplate',
    testSql: 'testSql',
    connectTimeoutMs: 'connectTimeoutMs',
    queryTimeoutMs: 'queryTimeoutMs',
} as const

export const DatabaseOutboundConfigResponseFields = {
    id: 'id',
    databaseType: 'databaseType',
    jdbcUrl: 'jdbcUrl',
    driverClass: 'driverClass',
    username: 'username',
    passwordRef: 'passwordRef',
    grantSqlTemplate: 'grantSqlTemplate',
    revokeSqlTemplate: 'revokeSqlTemplate',
    testSql: 'testSql',
    connectTimeoutMs: 'connectTimeoutMs',
    queryTimeoutMs: 'queryTimeoutMs',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const DatabaseOutboundConfigUpdateRequestFields = {
    databaseType: 'databaseType',
    jdbcUrl: 'jdbcUrl',
    driverClass: 'driverClass',
    username: 'username',
    passwordRef: 'passwordRef',
    grantSqlTemplate: 'grantSqlTemplate',
    revokeSqlTemplate: 'revokeSqlTemplate',
    testSql: 'testSql',
    connectTimeoutMs: 'connectTimeoutMs',
    queryTimeoutMs: 'queryTimeoutMs',
    version: 'version',
} as const

export const EmailOutboundConfigCreateRequestFields = {
    smtpHost: 'smtpHost',
    smtpPort: 'smtpPort',
    smtpUsername: 'smtpUsername',
    smtpPasswordRef: 'smtpPasswordRef',
    useTls: 'useTls',
    fromAddress: 'fromAddress',
    toAddresses: 'toAddresses',
    ccAddresses: 'ccAddresses',
    grantSubjectTemplate: 'grantSubjectTemplate',
    grantBodyTemplate: 'grantBodyTemplate',
    revokeSubjectTemplate: 'revokeSubjectTemplate',
    revokeBodyTemplate: 'revokeBodyTemplate',
    bodyContentType: 'bodyContentType',
    connectTimeoutMs: 'connectTimeoutMs',
} as const

export const EmailOutboundConfigResponseFields = {
    id: 'id',
    smtpHost: 'smtpHost',
    smtpPort: 'smtpPort',
    smtpUsername: 'smtpUsername',
    smtpPasswordRef: 'smtpPasswordRef',
    useTls: 'useTls',
    fromAddress: 'fromAddress',
    toAddresses: 'toAddresses',
    ccAddresses: 'ccAddresses',
    grantSubjectTemplate: 'grantSubjectTemplate',
    grantBodyTemplate: 'grantBodyTemplate',
    revokeSubjectTemplate: 'revokeSubjectTemplate',
    revokeBodyTemplate: 'revokeBodyTemplate',
    bodyContentType: 'bodyContentType',
    connectTimeoutMs: 'connectTimeoutMs',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const EmailOutboundConfigUpdateRequestFields = {
    smtpHost: 'smtpHost',
    smtpPort: 'smtpPort',
    smtpUsername: 'smtpUsername',
    smtpPasswordRef: 'smtpPasswordRef',
    useTls: 'useTls',
    fromAddress: 'fromAddress',
    toAddresses: 'toAddresses',
    ccAddresses: 'ccAddresses',
    grantSubjectTemplate: 'grantSubjectTemplate',
    grantBodyTemplate: 'grantBodyTemplate',
    revokeSubjectTemplate: 'revokeSubjectTemplate',
    revokeBodyTemplate: 'revokeBodyTemplate',
    bodyContentType: 'bodyContentType',
    connectTimeoutMs: 'connectTimeoutMs',
    version: 'version',
} as const

export const K8sOutboundConfigCreateRequestFields = {
    apiServerUrl: 'apiServerUrl',
    authType: 'authType',
    tokenRef: 'tokenRef',
    caCertRef: 'caCertRef',
    defaultNamespace: 'defaultNamespace',
    skipTlsVerify: 'skipTlsVerify',
    connectTimeoutMs: 'connectTimeoutMs',
    requestTimeoutMs: 'requestTimeoutMs',
} as const

export const K8sOutboundConfigResponseFields = {
    id: 'id',
    apiServerUrl: 'apiServerUrl',
    authType: 'authType',
    tokenRef: 'tokenRef',
    caCertRef: 'caCertRef',
    defaultNamespace: 'defaultNamespace',
    skipTlsVerify: 'skipTlsVerify',
    connectTimeoutMs: 'connectTimeoutMs',
    requestTimeoutMs: 'requestTimeoutMs',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const K8sOutboundConfigUpdateRequestFields = {
    apiServerUrl: 'apiServerUrl',
    authType: 'authType',
    tokenRef: 'tokenRef',
    caCertRef: 'caCertRef',
    defaultNamespace: 'defaultNamespace',
    skipTlsVerify: 'skipTlsVerify',
    connectTimeoutMs: 'connectTimeoutMs',
    requestTimeoutMs: 'requestTimeoutMs',
    version: 'version',
} as const

export const LdapOutboundConfigCreateRequestFields = {
    host: 'host',
    port: 'port',
    useSsl: 'useSsl',
    useStarttls: 'useStarttls',
    bindDn: 'bindDn',
    bindPasswordRef: 'bindPasswordRef',
    baseDn: 'baseDn',
    userSearchBase: 'userSearchBase',
    userSearchFilter: 'userSearchFilter',
    groupSearchBase: 'groupSearchBase',
    groupMemberAttribute: 'groupMemberAttribute',
    userDnAttribute: 'userDnAttribute',
    connectTimeoutMs: 'connectTimeoutMs',
    responseTimeoutMs: 'responseTimeoutMs',
    poolSize: 'poolSize',
    dnTemplate: 'dnTemplate',
    defaultOu: 'defaultOu',
    objectClasses: 'objectClasses',
    ouRoutingRules: 'ouRoutingRules',
    directoryDialect: 'directoryDialect',
} as const

export const LdapOutboundConfigResponseFields = {
    id: 'id',
    host: 'host',
    port: 'port',
    useSsl: 'useSsl',
    useStarttls: 'useStarttls',
    bindDn: 'bindDn',
    baseDn: 'baseDn',
    userSearchBase: 'userSearchBase',
    userSearchFilter: 'userSearchFilter',
    groupSearchBase: 'groupSearchBase',
    groupMemberAttribute: 'groupMemberAttribute',
    userDnAttribute: 'userDnAttribute',
    connectTimeoutMs: 'connectTimeoutMs',
    responseTimeoutMs: 'responseTimeoutMs',
    poolSize: 'poolSize',
    dnTemplate: 'dnTemplate',
    defaultOu: 'defaultOu',
    objectClasses: 'objectClasses',
    ouRoutingRules: 'ouRoutingRules',
    directoryDialect: 'directoryDialect',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const LdapOutboundConfigUpdateRequestFields = {
    host: 'host',
    port: 'port',
    useSsl: 'useSsl',
    useStarttls: 'useStarttls',
    bindDn: 'bindDn',
    bindPasswordRef: 'bindPasswordRef',
    baseDn: 'baseDn',
    userSearchBase: 'userSearchBase',
    userSearchFilter: 'userSearchFilter',
    groupSearchBase: 'groupSearchBase',
    groupMemberAttribute: 'groupMemberAttribute',
    userDnAttribute: 'userDnAttribute',
    connectTimeoutMs: 'connectTimeoutMs',
    responseTimeoutMs: 'responseTimeoutMs',
    poolSize: 'poolSize',
    version: 'version',
    dnTemplate: 'dnTemplate',
    defaultOu: 'defaultOu',
    objectClasses: 'objectClasses',
    ouRoutingRules: 'ouRoutingRules',
    directoryDialect: 'directoryDialect',
} as const

export const RestOutboundConfigCreateRequestFields = {
    baseUrl: 'baseUrl',
    authType: 'authType',
    authSecretRef: 'authSecretRef',
    tokenUrl: 'tokenUrl',
    clientId: 'clientId',
    clientSecretRef: 'clientSecretRef',
    scope: 'scope',
    grantMethod: 'grantMethod',
    grantUrlTemplate: 'grantUrlTemplate',
    grantBodyTemplate: 'grantBodyTemplate',
    grantHeaders: 'grantHeaders',
    revokeMethod: 'revokeMethod',
    revokeUrlTemplate: 'revokeUrlTemplate',
    revokeBodyTemplate: 'revokeBodyTemplate',
    revokeHeaders: 'revokeHeaders',
    successStatusCodes: 'successStatusCodes',
    contentType: 'contentType',
    connectTimeoutMs: 'connectTimeoutMs',
    readTimeoutMs: 'readTimeoutMs',
} as const

export const RestOutboundConfigResponseFields = {
    id: 'id',
    baseUrl: 'baseUrl',
    authType: 'authType',
    authSecretRef: 'authSecretRef',
    tokenUrl: 'tokenUrl',
    clientId: 'clientId',
    clientSecretRef: 'clientSecretRef',
    scope: 'scope',
    grantMethod: 'grantMethod',
    grantUrlTemplate: 'grantUrlTemplate',
    grantBodyTemplate: 'grantBodyTemplate',
    grantHeaders: 'grantHeaders',
    revokeMethod: 'revokeMethod',
    revokeUrlTemplate: 'revokeUrlTemplate',
    revokeBodyTemplate: 'revokeBodyTemplate',
    revokeHeaders: 'revokeHeaders',
    successStatusCodes: 'successStatusCodes',
    contentType: 'contentType',
    connectTimeoutMs: 'connectTimeoutMs',
    readTimeoutMs: 'readTimeoutMs',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const RestOutboundConfigUpdateRequestFields = {
    baseUrl: 'baseUrl',
    authType: 'authType',
    authSecretRef: 'authSecretRef',
    tokenUrl: 'tokenUrl',
    clientId: 'clientId',
    clientSecretRef: 'clientSecretRef',
    scope: 'scope',
    grantMethod: 'grantMethod',
    grantUrlTemplate: 'grantUrlTemplate',
    grantBodyTemplate: 'grantBodyTemplate',
    grantHeaders: 'grantHeaders',
    revokeMethod: 'revokeMethod',
    revokeUrlTemplate: 'revokeUrlTemplate',
    revokeBodyTemplate: 'revokeBodyTemplate',
    revokeHeaders: 'revokeHeaders',
    successStatusCodes: 'successStatusCodes',
    contentType: 'contentType',
    connectTimeoutMs: 'connectTimeoutMs',
    readTimeoutMs: 'readTimeoutMs',
    version: 'version',
} as const

export const ScimOutboundConfigCreateRequestFields = {
    baseUrl: 'baseUrl',
    userEndpoint: 'userEndpoint',
    groupEndpoint: 'groupEndpoint',
    userIdAttribute: 'userIdAttribute',
    groupIdAttribute: 'groupIdAttribute',
    auth: 'auth',
    http: 'http',
    supportsBulk: 'supportsBulk',
    supportsEtag: 'supportsEtag',
    patchStyle: 'patchStyle',
} as const

export const ScimOutboundConfigResponseFields = {
    id: 'id',
    connectorId: 'connectorId',
    baseUrl: 'baseUrl',
    userEndpoint: 'userEndpoint',
    groupEndpoint: 'groupEndpoint',
    userIdAttribute: 'userIdAttribute',
    groupIdAttribute: 'groupIdAttribute',
    authType: 'authType',
    tokenUrl: 'tokenUrl',
    clientId: 'clientId',
    username: 'username',
    scope: 'scope',
    connectTimeoutMs: 'connectTimeoutMs',
    readTimeoutMs: 'readTimeoutMs',
    maxRetries: 'maxRetries',
    retryBackoffMs: 'retryBackoffMs',
    supportsBulk: 'supportsBulk',
    supportsEtag: 'supportsEtag',
    patchStyle: 'patchStyle',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const ScimOutboundConfigUpdateRequestFields = {
    baseUrl: 'baseUrl',
    userEndpoint: 'userEndpoint',
    groupEndpoint: 'groupEndpoint',
    userIdAttribute: 'userIdAttribute',
    groupIdAttribute: 'groupIdAttribute',
    auth: 'auth',
    http: 'http',
    supportsBulk: 'supportsBulk',
    supportsEtag: 'supportsEtag',
    patchStyle: 'patchStyle',
    version: 'version',
} as const

export const OutboundConnectorTabKeys = {
    id: 'id',
} as const

export const ScriptOutboundConfigResponseFields = {
    id: 'id',
    outboundConnectorId: 'outboundConnectorId',
    baseUrl: 'baseUrl',
    authType: 'authType',
    tokenUrl: 'tokenUrl',
    clientId: 'clientId',
    scope: 'scope',
    username: 'username',
    apiKeyHeader: 'apiKeyHeader',
    script: 'script',
    createEnabled: 'createEnabled',
    updateEnabled: 'updateEnabled',
    deleteEnabled: 'deleteEnabled',
    queryEnabled: 'queryEnabled',
    connectTimeoutMs: 'connectTimeoutMs',
    readTimeoutMs: 'readTimeoutMs',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const ScriptOutboundConfigCreateRequestFields = {
    baseUrl: 'baseUrl',
    authType: 'authType',
    authSecretRef: 'authSecretRef',
    tokenUrl: 'tokenUrl',
    clientId: 'clientId',
    clientSecretRef: 'clientSecretRef',
    scope: 'scope',
    username: 'username',
    passwordSecretRef: 'passwordSecretRef',
    apiKeyHeader: 'apiKeyHeader',
    apiKeySecretRef: 'apiKeySecretRef',
    script: 'script',
    createEnabled: 'createEnabled',
    updateEnabled: 'updateEnabled',
    deleteEnabled: 'deleteEnabled',
    queryEnabled: 'queryEnabled',
    connectTimeoutMs: 'connectTimeoutMs',
    readTimeoutMs: 'readTimeoutMs',
} as const

export const ScriptOutboundConfigUpdateRequestFields = {
    version: 'version',
    baseUrl: 'baseUrl',
    authType: 'authType',
    authSecretRef: 'authSecretRef',
    tokenUrl: 'tokenUrl',
    clientId: 'clientId',
    clientSecretRef: 'clientSecretRef',
    scope: 'scope',
    username: 'username',
    passwordSecretRef: 'passwordSecretRef',
    apiKeyHeader: 'apiKeyHeader',
    apiKeySecretRef: 'apiKeySecretRef',
    script: 'script',
    createEnabled: 'createEnabled',
    updateEnabled: 'updateEnabled',
    deleteEnabled: 'deleteEnabled',
    queryEnabled: 'queryEnabled',
    connectTimeoutMs: 'connectTimeoutMs',
    readTimeoutMs: 'readTimeoutMs',
} as const
/* eslint-enable i18next/no-literal-string */

// ====================================================================================
// SAP_ABAP outbound config
// ====================================================================================

export interface SapAbapDefaultRole {
    roleName: string
    sequence: number
}

export interface SapAbapOutboundConfig {
    id: UUID
    odataBaseUrl: string
    rfcBaseUrl: string | null
    authType: string
    tokenUrl: string | null
    clientId: string | null
    scope: string | null
    username: string | null
    mandant: string
    userTypeDefault: string
    roleAssignmentMode: string
    compositeRoleExpansion: string
    bnameTemplate: string
    passwordPolicy: string
    lockReason: string
    etagRetryLimit: number
    bapiAllowlist: string | null
    createEnabled: boolean
    updateEnabled: boolean
    deleteEnabled: boolean
    enableEnabled: boolean
    disableEnabled: boolean
    grantEnabled: boolean
    revokeEnabled: boolean
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    defaultRoles: SapAbapDefaultRole[]
    version: number
}

export interface SapAbapOutboundConfigUpdateRequest {
    odataBaseUrl: string
    rfcBaseUrl?: string | null
    authType: string
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    username?: string | null
    passwordSecretRef?: string | null
    clientCertSecretRef?: string | null
    clientKeySecretRef?: string | null
    truststoreSecretRef?: string | null
    mandant: string
    userTypeDefault: string
    roleAssignmentMode: string
    compositeRoleExpansion: string
    bnameTemplate: string
    passwordPolicy: string
    lockReason: string
    etagRetryLimit: number
    bapiAllowlist?: string | null
    createEnabled: boolean
    updateEnabled: boolean
    deleteEnabled: boolean
    enableEnabled: boolean
    disableEnabled: boolean
    grantEnabled: boolean
    revokeEnabled: boolean
    connectTimeoutMs: number
    readTimeoutMs: number
    maxRetries: number
    defaultRoles?: SapAbapDefaultRoleEntry[] | null
    version: number
}

export interface SapAbapDefaultRoleEntry {
    roleName: string
    sequence: number
}

export interface SapAbapOutboundConfigCreateRequest {
    odataBaseUrl?: string | null
    rfcBaseUrl?: string | null
    authType?: AuthType | null
    tokenUrl?: string | null
    clientId?: string | null
    clientSecretRef?: string | null
    scope?: string | null
    username?: string | null
    passwordSecretRef?: string | null
    clientCertSecretRef?: string | null
    clientKeySecretRef?: string | null
    truststoreSecretRef?: string | null
    mandant?: string | null
    userTypeDefault?: string | null
    roleAssignmentMode?: string | null
    compositeRoleExpansion?: string | null
    bnameTemplate?: string | null
    passwordPolicy?: string | null
    lockReason?: string | null
    etagRetryLimit?: number | null
    bapiAllowlist?: string | null
    createEnabled?: boolean | null
    updateEnabled?: boolean | null
    deleteEnabled?: boolean | null
    enableEnabled?: boolean | null
    disableEnabled?: boolean | null
    grantEnabled?: boolean | null
    revokeEnabled?: boolean | null
    connectTimeoutMs?: number | null
    readTimeoutMs?: number | null
    maxRetries?: number | null
}
